import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';

// Generate or retrieve a stable session ID
function getSessionId(): string {
  const key = 'cc_session_id';
  let sid = sessionStorage.getItem(key);
  if (!sid) {
    sid = crypto.randomUUID();
    sessionStorage.setItem(key, sid);
  }
  return sid;
}

/**
 * Where the visit came from. AI assistants often strip the Referer header but
 * tag links with utm_source (e.g. ChatGPT adds utm_source=chatgpt.com), so
 * fall back to that. Consumed by the admin-analytics `traffic_sources` action.
 */
function externalReferrer(): string | null {
  if (document.referrer) return document.referrer;
  const utm = new URLSearchParams(window.location.search).get('utm_source');
  return utm ? `utm_source=${utm}` : null;
}

export function usePageTracking() {
  const location = useLocation();
  const lastPath = useRef<string>('');

  useEffect(() => {
    const path = location.pathname;
    if (path === lastPath.current) return;
    lastPath.current = path;

    const trackView = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        await supabase.from('page_views').insert({
          path,
          referrer: externalReferrer(),
          user_agent: navigator.userAgent,
          session_id: getSessionId(),
          user_id: user?.id ?? null,
        });
      } catch {
        // Silently fail — analytics should never break UX
      }
    };

    trackView();
  }, [location.pathname]);
}

export async function trackEvent(eventName: string, properties?: Record<string, unknown>) {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await (supabase as any).from('analytics_events').insert({
      event_name: eventName,
      properties: properties ?? {},
      session_id: getSessionId(),
      user_id: user?.id ?? null,
      path: window.location.pathname,
    });
  } catch {
    // Silently fail
  }
}
